import React, { createContext, useEffect, useReducer, useState } from "react";
import { useMsal } from "@azure/msal-react";
import fetchUserProfile from "../hooks/fetchUserProfile";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as microsoftTeams from "@microsoft/teams-js";
import msalInstance from "./msalConfig";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import UnauthorizedPage from "app/views/UnauthorizedPage";
import AxiosInstance1 from "app/hooks/AxiosInstance/AxiosInstance1";
// Initialize Fluent UI icons  

const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, isAuthenticated, isInitialized: true, user };
    }
    case "LOGIN":
      return { ...state, isAuthenticated: true, user: action.payload.user };
    case "LOGOUT":
      return { ...state, isAuthenticated: false, user: null };
    case "REGISTER":
      return { ...state, isAuthenticated: true, user: action.payload.user };
    default:
      return state;
  }
};

const AuthContext = createContext({
  ...initialState,
  logout: () => {},
  handleMicrosoftSignIn: () => {},
  handleMicrosoftSignIn1: () => {},
  handleTeamsAutoAuth: () => {},    // Added handleTeamsAutoAuth by Gauri for login in Microsoft Teams on 31 July 2025 
  // Added by Gauri for teams login with SSO enabled on 16 Oct 2025
  teamsNotAuthorized: false,
  setTeamsNotAuthorized: () => {},
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { instance } = useMsal();
  const [msalInitialized, setMsalInitialized] = useState(false);
  const navigate = useNavigate();
  const [isUnregistered, setIsUnregistered] = useState(false);
  const [timer, setTimer] = useState(10);
  const [token, setToken] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState(null);
  const [moduleAccess, setModuleAccess] = useState(null);
  const [refreshTokenTimeout, setRefreshTokenTimeout] = useState(null);
  // Added by Gauri for teams login with SSO enabled on 16 Oct 2025
  const [teamsNotAuthorized, setTeamsNotAuthorized] = useState(false);
  // Added by Gauri on 18 Nov 2025 to prevent toast caching issues
  const [toastContainerKey, setToastContainerKey] = useState(Date.now());

  useEffect(() => {
    const initializeMSAL = async () => {
      try {
        await msalInstance.initialize();
        setMsalInitialized(true);
      } catch (error) {
        console.error("MSAL initialization failed:", error);
      }
    };

    initializeMSAL();
  }, []);

  // Added by Gauri on 18 Nov 2025 to prevent toast caching issues
  // Clear toasts and reset container on mount to prevent caching issues
  useEffect(() => {
    // Clear any existing toasts when component mounts
    toast.dismiss();
    
    // Force re-render of ToastContainer by updating key
    // This ensures fresh initialization and prevents caching issues
    const timer = setTimeout(() => {
      setToastContainerKey(Date.now());
    }, 100);
    
    return () => {
      clearTimeout(timer);
      // Clear toasts on unmount
      toast.dismiss();
    };
  }, []);

  // Added by Gauri isRunningInTeams to check if the app is running in Microsoft Teams on 31 July 2025
  // const isRunningInTeams = () => {
  //   return (
  //     window.parent !== window.self || // iframe
  //     navigator.userAgent.includes("Teams/") || // Desktop Teams app
  //     window.location.hostname.includes("teams.microsoft.com") // Web Teams
  //   );
  // };

  // Modified by Gauri isRunningInTeams to check if the app is running in Microsoft Teams on 17 Oct 2025
  const isRunningInTeams = async () => {
    try {
      // First, try the Microsoft Teams SDK approach
      await microsoftTeams.app.initialize();
      const context = await microsoftTeams.app.getContext();
      // const sdkResult = context?.app?.host?.name?.toLowerCase() === "teams";
      // if (sdkResult) {
      //   return true;
      // }
      // Modified by Gauri to detect both Teams Web and Desktop on 17-10-2025
      const hostName = context?.app?.host?.name?.toLowerCase();
      if (hostName === "teams" || hostName === "desktop") {
        console.log("Teams (Web or Desktop) detected via SDK");
        return true;
      }
    } catch (err) {
      console.warn("Teams SDK not available:", err);
    }

    // Fallback detection methods for Desktop Teams and other scenarios
    const fallbackChecks = [
      () => window.parent !== window.self, // iframe (Teams Web)
      () => navigator.userAgent.includes("Teams/"), // Teams Desktop
      () => window.location.hostname.includes("teams.microsoft.com"), // Teams Web
      () => navigator.userAgent.includes("Electron") && navigator.userAgent.includes("Teams"), // Teams Desktop
      () => navigator.userAgent.toLowerCase().includes("electron") && navigator.userAgent.toLowerCase().includes("teams"),
      () => window.external && typeof window.external.invoke === "function", // Teams Desktop
      () => document.referrer.includes("teams.microsoft.com") || document.referrer.includes("teams.live.com"), // Referrer
      () => typeof window.microsoftTeams !== "undefined", // Teams SDK object
      () => window.location.search.includes("teams") || window.location.hash.includes("teams"), // URL context
      () => navigator.userAgent.includes("Mozilla/5.0") && 
            (navigator.userAgent.includes("Windows") || navigator.userAgent.includes("Mac")) &&
            !navigator.userAgent.includes("Chrome") && 
            !navigator.userAgent.includes("Safari") &&
            !navigator.userAgent.includes("Firefox") // Teams Desktop pattern
    ];

    return fallbackChecks.some(check => check());
  };

  // const showProviderLogin = (settings.showProviderLogin) ? "SSO Enabled" : "SSO Disabled";
  // console.log("showProviderLogin:", showProviderLogin);

  // Centralized Teams "Not Authorized" notice (as requested)
  const TeamsNotAuthorizedNotice = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        textAlign: "center",
      }}
    >
      <div>
        <h3>SSO is not enabled, so you are unable to log in.</h3>
        <p style={{ fontSize: "16px" }}>Please contact the administrator.</p>
        {/* <p>Please contact your administrator to enable Teams SSO access.</p> */}
      </div>
    </div>
  );

  console.log("teamsNotAuthorized in Teams:", teamsNotAuthorized);
  
  useEffect(() => {
    const initialize = async () => {
      // Added by Gauri isRunningInTeams to clear the session storage in Microsoft Teams on 31 July 2025
      const inTeams = await isRunningInTeams();
      if(inTeams){
        sessionStorage.removeItem("UserProfilePic");
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("selectedPath");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
      }

      const token = sessionStorage.getItem("access_token");
      const user = sessionStorage.getItem("user");

      if (user) {
        const parsedUser = JSON.parse(user);
        if (parsedUser === null) {
          setIsUnregistered(true);
        } else {
          dispatch({
            type: "LOGIN",
            payload: { isAuthenticated: true, user: parsedUser },
          });
        }
      } else if (token) {
        dispatch({
          type: "INIT",
          payload: { isAuthenticated: true, user: null },
        });
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (isUnregistered) {
      const countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 5000);

      setTimeout(() => {
        clearInterval(countdown);
        sessionStorage.clear();
        window.location.href = "/signin"; // Redirect and refresh the page immediately
      }, 5000); // Redirect after 0 seconds

      return () => clearInterval(countdown); // Cleanup the interval on component unmount
    }
  }, [isUnregistered, navigate]);

  const handleMicrosoftSignIn = async () => {
    if (!msalInitialized) {
      console.error("MSAL is not initialized.");
      return;
    }

    try {
      // Added by Gauri to Always clear the MSAL cache to force fresh authentication on 19 Nov 2025
      // This ensures the user always sees the Microsoft authentication prompt
      const cachedAccounts = instance.getAllAccounts();
      if (cachedAccounts.length > 0) {
        console.log("Clearing MSAL cache to force fresh authentication");
        instance.clearCache();
      }

      // Always use "login" prompt to force authentication
      // This ensures the user always sees the Microsoft sign-in page
      const loginResponse = await instance.loginPopup({
        // scopes: ["api://de2da8f7-0b6a-4fe4-8aa0-b49764b201a5/access_as_user"],
        scopes: window?.runtimeConfig?.scopes,
        // scopes: ["api://7185a779-a29f-423d-8b66-40cb92dfb5f3/react_UserAccess"],
        //scopes: ["api://42813dd3-c638-4cea-9eb5-4ee22dcc4fff/react_UserAccess"],
        //scopes: ["api://ini-sales.whizible.com/8a2c1a76-c99f-4235-b95b-43da1016e96d/access_as_user"],
        // scopes: ["api://de2da8f7-0b6a-4fe4-8aa0-b49764b201a5/access_as_user"],
        // prompt: "select_account",
        prompt: "login", // Modified by Gauri to Always force login to show authentication prompt on 11 Nov 2025
      });

      if (loginResponse.accessToken) {
        const decodedToken = jwtDecode(loginResponse.accessToken);
        const email = decodedToken.preferred_username;

        sessionStorage.setItem("email", email);
        sessionStorage.setItem("access_token", loginResponse.accessToken);
        sessionStorage.setItem("refresh_token", loginResponse.idToken);
        const user = await fetchUserProfile(loginResponse.accessToken);

        if (user) {
          await sessionStorage.setItem("user", JSON.stringify(user));
          dispatch({ type: "LOGIN", payload: { isAuthenticated: true, user } });
          toast.success("Microsoft login successful");
          setTimeout(() => {
            navigate("/landingPage");
          }, 3000);
        } else {
          setIsUnregistered(true);
        }
      }
    } catch (error) {
      dispatch({
        type: "INIT",
        payload: { isAuthenticated: false, user: null },
      });
      console.error("Microsoft sign-in failed:", error);
      toast.error("Microsoft login failed. Please retry.");
    }
  };
  const parseJwt = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Failed to parse JWT:", error);
      return null;
    }
  };

  const handleMicrosoftSignIn1 = async () => {
    try {
      dispatch({ type: "LOGIN", payload: { isAuthenticated: true } });

      await microsoftTeams.app.initialize();

      microsoftTeams.authentication.getAuthToken({
        successCallback: async (result) => {
          setToken(result);
          sessionStorage.setItem("token", result);
          const decodedToken = parseJwt(result);
          const username = decodedToken.preferred_username;

          const accounts = instance.getAllAccounts();
          const userAccount = accounts.find(
            (account) => account.username === username
          );
          sessionStorage.setItem("accounts", accounts);
          if (userAccount) {
            sessionStorage.setItem("userAccount", userAccount);
            instance.setActiveAccount(userAccount);
          } else {
            console.error("User account not found in MSAL accounts.");
          }
          dispatch({ type: "LOGIN", payload: { isAuthenticated: true } });

          toast.success("Microsoft login successful");
          // Decode the JWT token to get the preferred_username
          try {
            const decodedToken = jwtDecode(result);
            const emailId = decodedToken.preferred_username;

            // Call the API with the Bearer token
            // api/ModuleAccess/Get?empID=${empID}
            // `${window.runtimeConfig.REACT_APP_BASEURL_ACCESS_CONTROL1}/api/UserProfile/Get?emailId=saji@whizible.net`,
            const userProfileResponse = await fetch(
              `${window.runtimeConfig.REACT_APP_BASEURL_ACCESS_CONTROL1}/api/UserProfile/Get?emailId=${emailId}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${result}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (!userProfileResponse.ok) {
              throw new Error(
                `Error fetching user profile: ${userProfileResponse.statusText}`
              );
            }

            const userProfileData = await userProfileResponse.json();
            setUserProfile(userProfileData);
            sessionStorage.setItem("user", JSON.stringify(userProfileData));
            sessionStorage.setItem(
              "UserProfilePic",
              userProfileData?.data?.profilePicURL
            );
            // Extract empID from userProfileData
            const empID = userProfileData.data.employeeId;
            if (!empID) setIsUnregistered(true);
            // Call the second API with empID

            const url = `/api/ModuleAccess/Get?empID=${empID}`;
            const moduleAccessResponse = await AxiosInstance1.get(url, {
              employeeId: empID,
            });

            // const moduleAccessResponse = await fetch(
            //   `${window.runtimeConfig.REACT_APP_BASEURL_ACCESS_CONTROL1}/api/ModuleAccess/Get?empID=${empID}`,
            //   // `https://pms.whizible.com/W24_Teams_API/`,
            //   // `https://pms.whizible.com/W24_Teams_API/api/ModuleAccess/Get?empID=${empID}`,
            //   {
            //     method: "GET",
            //     headers: {
            //       Authorization: `Bearer ${result}`,
            //       "Content-Type": "application/json",
            //     },
            //   }
            // );

            if (!moduleAccessResponse.status === 200) {
              throw new Error(
                `Error fetching module access: ${moduleAccessResponse.statusText}`
              );
            }

            const moduleAccessData = await moduleAccessResponse.data;
            setModuleAccess(moduleAccessData);
            sessionStorage.setItem("tAccess", JSON.stringify(moduleAccessData));
            const empId =
              moduleAccessData.data.length > 0
                ? moduleAccessData.data[0].empId
                : null;
            if (emailId) navigate("/landingPage");
          } catch (apiError) {
            setError("Error calling API: " + apiError.message);
            // Clear cache if userProfileData is null
            if (!userProfile) {
              sessionStorage.clear();
              toast.error("You are not a registered user");
            }
          }
        },
        failureCallback: (error) => {
          setError("Error getting token: " + error);
        },
      });
    } catch (err) {
      setError("Initialization error: " + err.message);
    }
  };

  // Added Teams Login by Gauri on 31 July 2025
  const handleTeamsAutoAuth = async () => {
    try {
      await microsoftTeams.app.initialize();
      await microsoftTeams.app.getContext(); // Optional but safer

      const token = await new Promise((resolve, reject) => {
        microsoftTeams.authentication.getAuthToken({
          successCallback: resolve,
          failureCallback: reject,
        });
      });

      console.log("Teams token received");
      const decodedToken = await parseJwt(token);
      const emailId = decodedToken.preferred_username;

      sessionStorage.setItem("token", token);
      sessionStorage.setItem("access_token", token);
      sessionStorage.setItem("email", emailId);

      const user = await fetchUserProfile(decodedToken);

      if (user) {
        await sessionStorage.setItem("user", JSON.stringify(user));
        dispatch({ type: "LOGIN", payload: { isAuthenticated: true, user } });
        toast.success("Microsoft login successful");
        // Redirect after a short delay to ensure the loading spinner is visible
        setTimeout(() => {
          navigate("/landingPage");
        }, 3000);
      } else {
        // User is not registered - set isUnregistered to show proper message
        setIsUnregistered(true);
      }
    } catch (error) {
      console.error("Teams auto-auth failed:", error);
      sessionStorage.clear();
      // Don't set isUnregistered here - let the calling component handle the error
      throw error; // Re-throw the error so JwtLogin can catch it
    }
  };
  //Teams Login Ends Here

  const login = async (username, encryptedPassword, data) => {
    // try {
    //   const response = await fetch(
    //     `${
    //       window.runtimeConfig.REACT_APP_BASEURL_ACCESS_CONTROL1
    //     }/api/Login/LoginAPI?UserName=${encodeURIComponent(
    //       username
    //     )}&PassWord=${encodeURIComponent(encryptedPassword)}`,
    //     {
    //       method: "POST",
    //       headers: {
    //         "Content-Type": "application/json",
    //       },
    //     }
    //   );

    // const responseData = await response.json();
    // const responseData = data;

    // if (!response.ok) {
    //   // var response1 = await response.json();
    //   if (responseData?.type === 1 && responseData?.data) {
    //     toast.error(`${responseData.data}`);
    //   } else if (responseData?.type === 1) {
    //     toast.error(`Something WentWrong`);
    //   }

    //   // console.log(responseData);
    //   throw new Error("Invalid username or password");
    //   return;
    // }

    // const data = await response.text();
    // const data = await response.json();
    // const data = data;
    const token = `${data?.data?.token}`;
    if (!token) {
      toast.error("Failed To generate token");
      return;
    }

    const decodedToken = jwtDecode(token);
    sessionStorage.setItem("email", decodedToken.preferred_username);
    sessionStorage.setItem("access_token", token);

    const user = await fetchUserProfile(token);

    if (user) {
      await sessionStorage.setItem("user", JSON.stringify(user));
      await dispatch({ type: "LOGIN", payload: { isAuthenticated: true, user } });
      // toast.success("Microsoft login successful");
      toast.success("Login Successfully!");
      navigate("/landingPage");
      // setTimeout(() => {
      // }, 3000);
    } else {
      setIsUnregistered(true);
    }
    // sessionStorage.setItem("user", JSON.stringify(user));

    // dispatch({ type: "LOGIN", payload: { isAuthenticated: true, user } });

    // navigate("/landingPage");
    // setTimeout(() => {
    // }, 3000);
  };

  // const logout = () => {
  //   debugger;
  //   var user = sessionStorage.getItem("user");
  //   let employeeData = JSON.parse(user);
  //   let empID = employeeData?.employeeId;
  //   const accessToken = sessionStorage.getItem("access_token");
  //   const requestData = {
  //     employeeId: empID,
  //   };

  //   var url = `${window.runtimeConfig.REACT_APP_BASEURL_ACCESS_CONTROL1}/api/Login/LogoutAPI?EmployeeID=${empID}`;
  //   sessionStorage.clear();
  //   dispatch({ type: "LOGOUT" });
  //   // toast changed By Madhuri.K on 11-04-2025
  //   toast.success("Logged out successfully !");
  // };

  // useEffect(() => {
  //   debugger;
  //   const handleUnload = () => {
  //     // const encryptedPassword = encryptPassword();
  //     // const encryptedPassword = 'encryptPassword();'
  //     // const logoutUrl = `${API_BASE_URL}/LogoutAPI?UserName=${encodeURIComponent(formData.username)}&PassWord=${encodeURIComponent(encryptedPassword)}`;

  //     // Send logout signal without blocking unload
  //     navigator.sendBeacon();
  //   };

  //   window.addEventListener("beforeunload", handleUnload);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleUnload);
  //   };
  // }, []);

  const logout = async () => {
    try {
      const user = sessionStorage.getItem("user");
      const accessToken = sessionStorage.getItem("access_token");

      if (user && accessToken) {
        const employeeData = JSON.parse(user);
        const empID = employeeData?.employeeId;

        // const url = `${window.runtimeConfig.REACT_APP_BASEURL_ACCESS_CONTROL1}/api/Login/LogoutAPI?EmployeeID=${empID}`;
        const url = `/api/Login/LogoutAPI?EmployeeID=${empID}`;

        const response = await AxiosInstance1.post(url,
          { employeeId: empID }
        );

        // var response = await fetch(url, {
        //   method: "POST",
        //   headers: {
        //     "Content-Type": "application/json",
        //     Authorization: `Bearer ${accessToken}`,
        //   },
        //   body: JSON.stringify({ employeeId: empID }),
        // });

        // Optionally, parse the response body
        const result = await response.json();
        if ((result.message = "LogoutSuccesful" && result.type === 0)) {
          toast.success(`${result.data}`);
        }

        // return result; // <-- Return the API response
      }
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      // Added by Gauri to Clear MSAL accounts silently (no popup) when Logout on 11 Nov 2025
      try {
        if (msalInitialized && instance) {
          // Clear all accounts from MSAL cache silently
          // This will force re-authentication on next login without showing any popup
          instance.clearCache();
          console.log("MSAL cache cleared successfully");
        }
      } catch (msalError) {
        console.error("MSAL cache clear failed:", msalError);
        // Even if MSAL cache clear fails, continue with clearing session storage
      }

      // Clear session storage
      sessionStorage.clear();

      // Dispatch logout action
      dispatch({ type: "LOGOUT" });

      // Show logout success message
    }
  };

  if (isUnregistered) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          textAlign: "center",
        }}
      >
        <div>
          <h2>You are not a registered user</h2>
          <p>Redirecting to sign-in page...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        logout,
        login, //Added by Parth on 21-04-2025 for base login feature
        handleMicrosoftSignIn,
        handleMicrosoftSignIn1,
        handleTeamsAutoAuth,    // Added handleTeamsAutoAuth by Gauri for login in Microsoft Teams on 31 July 2025
        // Added by Gauri for teams login with SSO enabled on 16 Oct 2025
        teamsNotAuthorized,
        setTeamsNotAuthorized,
      }}
    >
      <ToastContainer position="top-right" autoClose={5000} />
      {/* Added by Gauri for teams login with SSO enabled on 16 Oct 2025 */}
      {teamsNotAuthorized ? (
        <TeamsNotAuthorizedNotice />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
